import type { Cliente, Cotacao } from '@/db';
import { supabase } from '@/services/supabaseClient';
import { buildCotacaoPdf, cotacaoPdfFilename } from '@/utils/cotacaoPdf';

export type QuoteEmailResult = { ok: true; recipient: string } | { ok: false; error: string };

function toBase64(bytes: Uint8Array) {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}

// Envia o PDF da cotação para o e-mail do próprio vendedor logado. O destinatário
// é resolvido no servidor (zsb_users.email do usuário autenticado), nunca pelo navegador.
export async function sendCotacaoEmail(cotacao: Cotacao, cliente: Cliente | null, options: { manual?: boolean } = {}): Promise<QuoteEmailResult> {
  if (!supabase) return { ok: false, error: 'Supabase não configurado.' };
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) return { ok: false, error: 'sessão expirada.' };
  let pdf: Uint8Array;
  try {
    pdf = await buildCotacaoPdf(cotacao, cliente);
  } catch (error) {
    console.error('Falha ao gerar o PDF da cotação para o e-mail:', error);
    return { ok: false, error: `não foi possível gerar o PDF (${error instanceof Error ? error.message : 'erro desconhecido'}).` };
  }
  try {
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/zsb-quote-email`, {
      method: 'POST',
      headers: { apikey: import.meta.env.VITE_SUPABASE_ANON_KEY, Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        // Envio manual usa chave nova: senão o provedor trata como repetido e não reenvia.
        quoteKey: `${cotacao.id}-${cotacao.createdAt}${options.manual ? `-m${Date.now()}` : ''}`,
        clienteNome: cotacao.clienteNome,
        clienteCodigo: cotacao.clienteCodigo,
        filename: cotacaoPdfFilename(cotacao),
        pdfBase64: toBase64(pdf),
      }),
    });
    const payload = await response.json().catch(() => ({}));
    return response.ok ? { ok: true, recipient: payload.recipient } : { ok: false, error: payload.error || payload.message || `não foi possível enviar (código ${response.status}).` };
  } catch (error) {
    console.error('Falha ao chamar o serviço de e-mail:', error);
    return { ok: false, error: `serviço de e-mail indisponível (${error instanceof Error ? error.message : 'erro desconhecido'}).` };
  }
}
